/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import type IntlService from "ember-intl/services/intl";
import type ForcJournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import type ConsultationReportWrittenQuestion from "parog-web/models/consultation/consultation-report-written-question";
import type Patient from "parog-web/models/patient";
import type ConfigurationService from "parog-web/services/configuration";
import { Configuration } from "parog-web/services/configuration";

interface ConsultationReportFormContentComponentArgs {
  forcComponent: ForcJournalEventBasedComponent;
  onUpdateQuestion?: () => void;
  setCurrentQuestions: (questions: ConsultationReportWrittenQuestion[]) => void;
  // When the consultation type comes from the dashboard, it is not in the dropdown list so it cannot be changed
  hideConsultationType: boolean;
  patient: Patient;
}

interface QuestionWithAnswer {
  question: ConsultationReportWrittenQuestion;
  answer: string;
}

export default class ConsultationReportFormContentComponent extends Component<ConsultationReportFormContentComponentArgs> {
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare configuration: ConfigurationService;

  get patient(): Patient {
    return this.args.patient;
  }

  @tracked declare questions: ConsultationReportWrittenQuestion[];

  constructor(
    owner: unknown,
    args: ConsultationReportFormContentComponentArgs
  ) {
    super(owner, args);
    // It is needed to update the questions
    // when the changeset is updated after draft loading
    args.forcComponent.afterLoad = () => {
      this.onConsultationTypeChange();
    };
  }
  get consultationType(): string {
    return this.args.forcComponent.changeset.get("consultationType");
  }

  get displayPPSActionsDropdown(): boolean {
    if (
      this.args.forcComponent.args.patient.followUpReasonRecord
        ?.therapeuticalArea.id != "mental"
    )
      return false;

    return this.consultationType?.startsWith(
      "Point de suivi récurrent Case Manager"
    );
  }

  @action
  onQuestionInput(): void {
    this.args.onUpdateQuestion?.();
  }

  @action
  async onConsultationTypeChange(): Promise<void> {
    if (this.consultationType == "Accompagnement thérapeutique") {
      this.args.forcComponent.changeset.set("consultationType", null);
      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: {
          tab: "write-therapeutic-assistance"
        }
      });
    } else {
      if (this.consultationType == undefined) {
        this.args.setCurrentQuestions?.([]);
      } else {
        // User can only fill questions
        // we indicated in ConsultationReportWrittenJournalEvent.getQuestions
        if (this.args.forcComponent.isNewMode) {
          const defaultQuestions =
            ConsultationReportWrittenJournalEvent.getQuestions(
              this.consultationType
            );
          this.args.setCurrentQuestions?.(defaultQuestions);
          // This is needed, otherwise changeset is recomputed when typing the first answer
          // of the first question, and we lose the focus from the textarea
          this.updateQuestionsFromChangeset(
            defaultQuestions.map(defaultQuestion => {
              return {
                question: defaultQuestion,
                answer: defaultQuestion.answer
              };
            }),
            false
          );
        }
        // If the user is editing the JE
        // it may want to see old questions that
        // are not provided anymore
        else {
          this.args.setCurrentQuestions(
            (
              this.args.forcComponent
                .model as ConsultationReportWrittenJournalEvent
            ).questions
          );
        }
      }
    }
  }

  get consultationTypesFromFrequencies(): string[] {
    const consultationFrequencies =
      this.configuration.configurationForPatient(
        Configuration.Consultation,
        this.patient
      ).consultationFrequencies || [];

    return consultationFrequencies
      .filter(type => type.enabled)
      .map(type => type.id);
  }

  get consultationTypes(): string[] {
    const consultationReportTypes =
      this.configuration.configurationForPatient(
        Configuration.Consultation,
        this.patient
      ).consultationReportTypes || [];

    const filteredReportTypes = consultationReportTypes.filter(
      reportType => reportType.enabled
    );

    return filteredReportTypes.map(type => {
      return this.intl.t(`consultation-report-types.${type.id}`);
    });
  }

  // As the `questions` property of the changeset is an array,
  // we have to manage it on our own
  @action
  updateQuestionsFromChangeset(
    questionsWithAnswer: QuestionWithAnswer[],
    replace = true
  ): void {
    const changesetQuestions = Object.values(
      Object.assign(
        {},
        this.args.forcComponent.changeset.get("questions")
      ) as ConsultationReportWrittenQuestion[]
    );

    questionsWithAnswer.forEach(questionWithAnswer => {
      const questionToUpdate = changesetQuestions.find(
        question => question.key == questionWithAnswer.question.key
      );
      if (questionToUpdate && replace) {
        questionToUpdate.answer = questionWithAnswer.answer;
      } else {
        if (!questionToUpdate) {
          changesetQuestions.push({
            key: questionWithAnswer.question.key,
            issue: questionWithAnswer.question.issue,
            answer: questionWithAnswer.answer
          });
        }
      }
    });
    this.args.forcComponent.changeset.set("questions", changesetQuestions);
  }

  @action
  updateQuestion(
    question: ConsultationReportWrittenQuestion,
    event: InputEvent
  ): void {
    this.updateQuestionsFromChangeset([
      { question, answer: (event.target as HTMLInputElement).value }
    ]);
    this.args.onUpdateQuestion?.();
  }
}
